import { useEffect, useState } from "react";
import StreamVideo, { hasTrailer } from "@/components/StreamVideo";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { naira, usd, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RefundNote from "@/components/RefundNote";
import Laurels from "@/components/Laurels";
import LaurelsEditor from "@/components/LaurelsEditor";
import ReleaseReminderButton from "@/components/ReleaseReminderButton";
import { genreLabel } from "@/hooks/useGenres";
import Navbar from "@/components/Navbar";
import FilmCard from "@/components/FilmCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar, Globe, User, Play, ShoppingCart, Sparkles, Gift, Film as FilmIcon, Wallet, Instagram, Twitter, Star, MessageSquare, Users, Heart } from "lucide-react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import FollowButton from "@/components/FollowButton";
import { StarRatingDisplay } from "@/components/StarRating";
import RatingDialog from "@/components/RatingDialog";
import WatchPartyDialog from "@/components/WatchPartyDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function FilmDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [creator, setCreator] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);
  const [owned, setOwned] = useState(false);
  const [geo, setGeo] = useState(null); // { country_code, is_diaspora, currency, diaspora_film_price_usd_cents }
  const [ratingSummary, setRatingSummary] = useState({ count: 0, average: 0 });
  const [reviews, setReviews] = useState([]);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [partyOpen, setPartyOpen] = useState(false);
  const [activeParty, setActiveParty] = useState(null);
  // Donation flow — donors pick a preset (or type a custom amount) and
  // we route them through Paystack. Not auth-gated past the first click;
  // the init endpoint requires auth so the modal nudges to login if the
  // user isn't signed in.
  const [donateOpen, setDonateOpen] = useState(false);
  const [donateAmount, setDonateAmount] = useState(500);
  const [customDonate, setCustomDonate] = useState("");
  const [donating, setDonating] = useState(false);
  // Public monthly top-supporters shelf. Hides when empty so new films
  // don't show a lonely stub.
  const [topSupporters, setTopSupporters] = useState([]);
  const credit = user?.referral_credit_remaining || 0;
  const isOwner = user && film && film.filmmaker_id === user.id;
  const isAdmin = user && user.role === "admin";
  const hasFreeAccess = isOwner || isAdmin;
  const canRate = user?.role === "viewer" && owned;
  const eligibleCredit = !isOwner ? credit : 0;
  const discountedPrice = film
    ? Math.max(film.price_naira - Math.min(eligibleCredit, film.price_naira - 100), 100)
    : 0;
  // Three-way pricing — ``local`` (NGN), ``african`` (discounted USD),
  // ``diaspora`` (full USD). Fall back to legacy flat $1 when the film
  // doc is missing the per-film USD tiers (pre-migration uploads).
  const region = geo?.region || (geo?.is_diaspora ? "diaspora" : "local");
  const isDiaspora = region !== "local";
  const usdPriceCents = region === "african"
    ? (film?.african_price_usd_cents ?? geo?.diaspora_film_price_usd_cents ?? 100)
    : (film?.diaspora_price_usd_cents ?? geo?.diaspora_film_price_usd_cents ?? 100);
  const showDiscount = !isDiaspora && !owned && !hasFreeAccess && eligibleCredit > 0 && film && discountedPrice < film.price_naira;

  useEffect(() => {
    api.get(`/films/${id}`).then((r) => {
      setFilm(r.data);
      api.get(`/creators/${r.data.filmmaker_id}`).then((c) => setCreator(c.data)).catch(() => {});
    }).catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
    api.get(`/films/${id}/related`).then((r) => setRelated(r.data || [])).catch(() => {});
    // Detect location once per page load so we render the right currency.
    // If geo lookup fails, assume international (USD) — the server re-checks location on every payment anyway.
    api.get("/geo").then((r) => setGeo(r.data)).catch(() => setGeo({ region: "diaspora", is_diaspora: true, currency: "USD", detected: false }));
    // Rating summary + review list — public endpoints; no auth needed.
    api.get(`/films/${id}/ratings/summary`).then((r) => setRatingSummary(r.data || { count: 0, average: 0 })).catch(() => {});
    api.get(`/films/${id}/ratings`).then((r) => setReviews(r.data || [])).catch(() => {});
    api.get(`/films/${id}/top-supporters`).then((r) => setTopSupporters(r.data?.supporters || [])).catch(() => {});
    if (user) {
      api.get("/films/library").then((r) => {
        if (r.data.some((f) => f.id === id)) setOwned(true);
      }).catch(() => {});
      // If the viewer has an active watch party for this film, surface it
      // so they can re-share the link instead of paying again.
      api.get("/watch-party/mine").then((r) => {
        const mine = (r.data || []).find((p) => p.film_id === id);
        if (mine) setActiveParty(mine);
      }).catch(() => {});
    }
  }, [id, user]);

  const refreshRatings = async () => {
    try {
      const [s, list] = await Promise.all([
        api.get(`/films/${id}/ratings/summary`),
        api.get(`/films/${id}/ratings`),
      ]);
      setRatingSummary(s.data || { count: 0, average: 0 });
      setReviews(list.data || []);
    } catch (_e) { /* silent — non-critical UI refresh */ }
  };

  const handleBuy = async (useWallet = false) => {
    if (!user) {
      navigate("/login", { state: { from: `/film/${id}` } });
      return;
    }
    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/payments/initialize", {
        film_id: id,
        callback_url,
        use_wallet: useWallet,
        // Anchor the currency to what the server told us on /geo. Server
        // re-checks the IP so we can't fake diaspora status here.
        currency: isDiaspora ? "USD" : "NGN",
      });
      if (data.already_owned) {
        toast.success("You already own this film. Redirecting…");
        navigate(`/watch/${id}`);
        return;
      }
      if (data.wallet_paid) {
        toast.success("Paid with wallet — enjoy the film!");
        navigate(`/watch/${id}`);
        return;
      }
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  // Resolve the chosen donation amount — presets are plain numbers,
  // "custom" pulls from the input field. Returns null if invalid so the
  // caller can early-exit without starting Paystack.
  const resolvedDonateAmount = () => {
    if (donateAmount === "custom") {
      if (isDiaspora) {
        const cents = Math.round(parseFloat(customDonate) * 100);
        return Number.isFinite(cents) && cents >= 100 && cents <= 100000 ? cents : null;
      }
      const n = parseInt(customDonate, 10);
      return Number.isFinite(n) && n >= 100 ? n : null;
    }
    return donateAmount;
  };
  const donatePresets = isDiaspora ? [200, 500, 1000, 2000] : [500, 1000, 2000];
  const fmtDonate = (amt) => (isDiaspora ? `$${(amt / 100).toLocaleString()}` : `₦${amt.toLocaleString()}`);

  const handleDonate = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/film/${id}` } });
      return;
    }
    const amount = resolvedDonateAmount();
    if (!amount) {
      toast.error(isDiaspora ? "Enter a donation amount between $1 and $1,000." : "Enter a donation amount of at least ₦100.");
      return;
    }
    setDonating(true);
    try {
      const callback_url = `${window.location.origin}/donate/callback`;
      const { data } = await api.post(`/films/${id}/donate/init`, isDiaspora
        ? { currency: "USD", amount_usd_cents: amount, callback_url }
        : { currency: "NGN", amount_naira: amount, callback_url });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setDonating(false);
    }
  };

  const walletBalanceNGN = user?.wallet_balance_naira || 0;
  const walletBalanceUSD = user?.wallet_balance_usd_cents || 0;
  const finalPriceNGN = film ? (showDiscount ? discountedPrice : film.price_naira) : 0;
  const finalPriceUSD = usdPriceCents;
  const finalPriceLabel = isDiaspora ? usd(finalPriceUSD) : naira(finalPriceNGN);
  const canPayWithWallet = !!user && user.role === "viewer" && !owned && !hasFreeAccess && (
    isDiaspora ? walletBalanceUSD >= finalPriceUSD : walletBalanceNGN >= finalPriceNGN
  );

  if (!film) {
    return (
      <div className="min-h-screen bg-ink grain">
        <Navbar />
        <div className="pt-32 px-8 text-center text-foreground/60" data-testid={notFound ? "film-not-found" : "film-loading"}>
          {notFound ? "This film isn't available." : "Loading film…"}
        </div>
      </div>
    );
  }

  const posterUrl = `${API}/films/${film.id}/poster`;

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />

      {/* Backdrop */}
      <div className="relative pt-20" data-testid="film-detail">
        <div className="absolute inset-0 h-[600px] overflow-hidden">
          <img src={posterUrl} alt="" className="w-full h-full object-cover blur-2xl opacity-30 scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/80 to-ink" />
        </div>

        <div className="relative px-6 sm:px-12 lg:px-20 max-w-[1400px] mx-auto py-12">
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-10">
            {/* Poster */}
            <div className="aspect-[2/3] rounded-xl overflow-hidden border border-white/10 bg-surface shadow-2xl shadow-terracotta/10">
              <img src={posterUrl} alt={film.title} className="w-full h-full object-cover" />
            </div>

            {/* Info */}
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-terracotta/20 text-terracotta border border-terracotta/40" data-testid="film-genre-badge">{genreLabel(film)}</Badge>
                <Badge className="bg-white/5 text-foreground/80 border border-white/10">{film.language}</Badge>
                <Badge className="bg-white/5 text-foreground/80 border border-white/10">{film.country}</Badge>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight">{film.title}</h1>

              {ratingSummary.count > 0 && (
                <div className="flex items-center gap-2 -mt-2" data-testid="film-rating-badge">
                  <StarRatingDisplay value={ratingSummary.average} size={18} showValue count={ratingSummary.count} />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 -mt-2">
                <ShareButton
                  url={typeof window !== "undefined" ? `${window.location.origin}/api/share/film/${film.id}` : ""}
                  title={`${film.title} · Cinevoya`}
                  text={`Watch "${film.title}" by ${film.filmmaker_name} on Cinevoya — short films from Africa.`}
                  variant="outline"
                  size="sm"
                  className="border-white/15 hover:bg-white/5 h-9"
                  testId="film-share-btn"
                />
                {(isOwner || isAdmin) && <LaurelsEditor film={film} onSaved={(laurels) => setFilm((f) => ({ ...f, laurels }))} />}
              </div>

              <Laurels laurels={film.laurels} />

              <div className="flex flex-wrap gap-5 text-sm text-foreground/70">
                <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {film.filmmaker_name}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {film.duration_minutes} min</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {film.year}</span>
                <span className="flex items-center gap-1.5"><Globe className="w-4 h-4" /> {film.country}</span>
              </div>

              <p className="text-foreground/85 leading-relaxed text-base max-w-3xl">{film.synopsis}</p>

              {film.themes?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {film.themes.map((t) => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-surface border border-white/10 text-foreground/70">
                      #{t}
                    </span>
                  ))}
                </div>
              )}

              {(film.director || film.producer || film.cast) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 max-w-3xl pt-2" data-testid="film-credits">
                  {film.director && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-foreground/45 font-semibold">Director</p>
                      <p className="text-sm text-foreground/90 mt-0.5">{film.director}</p>
                    </div>
                  )}
                  {film.producer && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-foreground/45 font-semibold">Producer</p>
                      <p className="text-sm text-foreground/90 mt-0.5">{film.producer}</p>
                    </div>
                  )}
                  {film.cast && (
                    <div className="sm:col-span-3">
                      <p className="text-[10px] uppercase tracking-widest text-foreground/45 font-semibold">Cast</p>
                      <p className="text-sm text-foreground/90 mt-0.5">{film.cast}</p>
                    </div>
                  )}
                </div>
              )}

              {(film.instagram_handle || film.twitter_handle) && (
                <div className="flex flex-wrap items-center gap-2" data-testid="film-socials">
                  {film.instagram_handle && (
                    <a
                      href={`https://instagram.com/${film.instagram_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-white/10 text-xs text-foreground/80 hover:text-foreground hover:border-terracotta/60 transition-colors"
                      data-testid="film-instagram-link"
                    >
                      <Instagram className="w-3.5 h-3.5" />
                      <span>@{film.instagram_handle}</span>
                    </a>
                  )}
                  {film.twitter_handle && (
                    <a
                      href={`https://twitter.com/${film.twitter_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-white/10 text-xs text-foreground/80 hover:text-foreground hover:border-terracotta/60 transition-colors"
                      data-testid="film-twitter-link"
                    >
                      <Twitter className="w-3.5 h-3.5" />
                      <span>@{film.twitter_handle}</span>
                    </a>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4 pt-4">
                {/* Publishing-state gate: coming_soon and festival_run
                    render an informational card instead of the usual
                    rent/watch CTAs. The server also blocks the paid
                    purchase and video endpoints for these states. */}
                {film && (film.publishing_state === "coming_soon" || film.publishing_state === "festival_run") ? (
                  <div className="w-full p-5 rounded-xl border border-sky-400/30 bg-sky-500/5" data-testid="publishing-state-gate">
                    {film.publishing_state === "coming_soon" ? (
                      <>
                        <p className="text-xs uppercase tracking-widest text-sky-300 font-semibold flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" /> Coming Soon
                        </p>
                        <p className="font-display text-xl font-bold mt-2 leading-tight">
                          {film.release_date
                            ? `Releasing on ${new Date(String(film.release_date).slice(0, 10) + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`
                            : "A release date will be announced soon."}
                        </p>
                        <p className="text-xs text-foreground/55 mt-2">You can't watch it yet — the trailer below gives you a taste.</p>
                        <ReleaseReminderButton filmId={film.id} user={user} />
                        <button
                          type="button"
                          onClick={() => (user ? setPartyOpen(true) : navigate("/login", { state: { from: `/film/${id}` } }))}
                          className="mt-4 ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-terracotta/50 text-terracotta hover:bg-terracotta/10 text-sm font-semibold transition-colors"
                          data-testid="launch-party-btn"
                        >
                          <Users className="w-4 h-4" /> {activeParty?.launch ? "Manage Launch Watch Party" : "Book a Launch Watch Party"}
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold flex items-center gap-1.5">
                          🏆 Festival Run
                        </p>
                        <p className="font-display text-xl font-bold mt-2 leading-tight">
                          Currently touring film festivals.
                        </p>
                        <p className="text-xs text-foreground/55 mt-2">The video is locked while it travels the festival circuit. Follow us for the general release.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-3 rounded-lg bg-surface border border-gold/30">
                      <p className="text-xs text-foreground/50">Pay-per-view</p>
                      {isDiaspora ? (
                        <div className="flex items-baseline gap-2">
                          <p className="font-display text-2xl font-bold text-gold" data-testid="film-price-usd">{usd(finalPriceUSD)}</p>
                          <p className="text-[10px] uppercase tracking-widest text-foreground/40">Flat international rate</p>
                        </div>
                      ) : showDiscount ? (
                        <div className="flex items-baseline gap-2">
                          <p className="font-display text-2xl font-bold text-gold">{naira(discountedPrice)}</p>
                          <p className="text-sm text-foreground/40 line-through">{naira(film.price_naira)}</p>
                        </div>
                      ) : (
                        <p className="font-display text-2xl font-bold text-gold">{naira(film.price_naira)}</p>
                      )}
                    </div>

                {hasFreeAccess ? (
                  <Button
                    onClick={() => navigate(`/watch/${film.id}`)}
                    className="bg-terracotta hover:bg-ember text-white h-12 px-7"
                    data-testid="watch-now-btn"
                  >
                    <Play className="w-4 h-4" /> {isAdmin && !isOwner ? "Screen film" : "Watch now"}
                  </Button>
                ) : owned ? (
                  <Button
                    onClick={() => navigate(`/watch/${film.id}`)}
                    className="bg-terracotta hover:bg-ember text-white h-12 px-7"
                    data-testid="watch-now-btn"
                  >
                    <Play className="w-4 h-4" /> Watch now
                  </Button>
                ) : (
                  <>
                    {canPayWithWallet ? (
                      <Button
                        onClick={() => handleBuy(true)}
                        disabled={loading}
                        className="bg-gold hover:bg-gold/90 text-ink h-12 px-7 font-semibold"
                        data-testid="buy-with-wallet-btn"
                      >
                        <Wallet className="w-4 h-4" /> Pay with wallet · {finalPriceLabel}
                      </Button>
                    ) : (
                      <>
                        {user?.role === "viewer" && (
                          <Button
                            onClick={() => navigate("/wallet")}
                            className="bg-gold hover:bg-gold/90 text-ink h-12 px-7 font-semibold"
                            data-testid="topup-wallet-btn"
                          >
                            <Wallet className="w-4 h-4" /> Top up wallet
                          </Button>
                        )}
                        <Button
                          onClick={() => handleBuy(false)}
                          disabled={loading}
                          className={user?.role === "viewer"
                            ? "bg-white/5 border border-white/15 hover:bg-white/10 text-foreground h-12 px-7"
                            : "bg-terracotta hover:bg-ember text-white h-12 px-7"
                          }
                          data-testid="buy-film-btn"
                        >
                          <ShoppingCart className="w-4 h-4" />{" "}
                          {loading ? "Redirecting…" : `Pay with card · ${finalPriceLabel}`}
                        </Button>
                      </>
                    )}
                  </>
                )}
                {/* Watch Party — always available to logged-in viewers &
                    filmmakers (guests can join without an account). Sits
                    alongside the regular Rent/Watch CTAs. If the host
                    already has a live party for this film, the same
                    button re-opens the share dialog. */}
                {user && user.role !== "admin" && (
                  <Button
                    onClick={() => setPartyOpen(true)}
                    variant="ghost"
                    className="h-12 px-6 border border-terracotta/40 bg-terracotta/10 hover:bg-terracotta/20 text-terracotta hover:text-terracotta"
                    data-testid="watch-party-btn"
                  >
                    <Users className="w-4 h-4" />
                    {activeParty ? "Manage Watch Party" : "Watch Party"}
                  </Button>
                )}
                {/* Donate — tip the filmmaker. Shown to everyone except
                    the filmmaker themselves (prevented server-side too). */}
                {film && user?.id !== film.filmmaker_id && (
                  <Button
                    onClick={() => setDonateOpen(true)}
                    variant="ghost"
                    className="h-12 px-6 border border-pink-400/40 bg-pink-500/10 hover:bg-pink-500/20 text-pink-300 hover:text-pink-200"
                    data-testid="donate-btn"
                  >
                    <Heart className="w-4 h-4" /> Donate
                  </Button>
                )}
                {user?.role === "viewer" && !owned && !hasFreeAccess && (
                  <p className="basis-full text-xs text-foreground/55 flex items-center gap-1.5" data-testid="wallet-balance-hint">                    <Wallet className="w-3 h-3 text-gold" />{" "}
                    Wallet balance:{" "}
                    <span className="text-gold font-semibold">
                      {isDiaspora ? usd(walletBalanceUSD) : naira(walletBalanceNGN)}
                    </span>
                    {canPayWithWallet ? (
                      <span className="text-emerald-400 ml-1">· enough to rent instantly</span>
                    ) : isDiaspora ? (
                      <span className="ml-1">· top up {usd(Math.max(finalPriceUSD - walletBalanceUSD, 0))} more to pay from wallet, or use card below</span>
                    ) : (
                      <span className="ml-1">· top up {naira(Math.max(finalPriceNGN - walletBalanceNGN, 0))} more to pay from wallet, or use card below</span>
                    )}
                  </p>
                )}

                <p className="text-xs text-foreground/50">Lifetime access · rewatch any time</p>
                {!owned && !hasFreeAccess && <RefundNote className="-mt-2" testId="rent-refund-policy-link" />}

                {/* Top supporters shelf — monthly roll-up, hides itself
                    when there's nothing to show so new films don't
                    advertise emptiness. Encourages giving by making
                    recent donors visible. */}
                {topSupporters.length > 0 && (
                  <div className="basis-full mt-3 flex items-center gap-2 flex-wrap" data-testid="top-supporters-shelf">
                    <span className="text-[10px] uppercase tracking-widest text-pink-300 font-semibold flex items-center gap-1">
                      <Heart className="w-3 h-3 fill-pink-400 text-pink-400" /> Top supporters this month
                    </span>
                    {topSupporters.map((s, i) => (
                      <span
                        key={`${s.donor_name}-${i}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-500/10 border border-pink-400/30 text-pink-200 text-xs"
                        title={`${s.donor_name} · ${s.count} donation${s.count === 1 ? "" : "s"} · ${[s.total_naira ? naira(s.total_naira) : null, s.total_usd_cents ? usd(s.total_usd_cents) : null].filter(Boolean).join(" + ") || naira(0)} total`}
                        data-testid={`top-supporter-${i}`}
                      >
                        <span className="font-semibold">{s.donor_name}</span>
                        <span className="text-foreground/50 text-[10px]">·</span>
                        <span className="font-display font-bold">{s.total_naira || !s.total_usd_cents ? naira(s.total_naira) : usd(s.total_usd_cents)}</span>
                      </span>
                    ))}
                  </div>
                )}
                  </>
                )}
              </div>

              {showDiscount && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs" data-testid="discount-banner">
                  <Gift className="w-3.5 h-3.5" />
                  Referral bonus: {naira(film.price_naira - discountedPrice)} off applied at checkout
                </div>
              )}

              {user && (user.id === film.filmmaker_id || user.role === "admin") && (
                <Link to={`/filmmaker/analyze/${film.id}`} className="inline-flex items-center gap-2 text-sm text-gold hover:text-terracotta mt-4 transition-colors" data-testid="link-analyze-festivals">
                  <Sparkles className="w-4 h-4" /> Analyse festival fit with AI
                </Link>
              )}
            </div>
          </div>

          {/* TRAILER — only renders when filmmaker has uploaded one */}
          {hasTrailer(film) && (
            <div className="mt-12" data-testid="film-trailer-section">
              <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-3 flex items-center gap-1.5">
                <Play className="w-3 h-3" /> Trailer
              </p>
              <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10 max-w-4xl">
                <StreamVideo
                  playbackPath={`/films/${film.id}/trailer-playback`}
                  legacySrc={film.trailer_path ? `${API}/films/${film.id}/trailer${film.trailer_uploaded_at ? `?v=${encodeURIComponent(film.trailer_uploaded_at)}` : ""}` : null}
                  controls
                  playsInline
                  preload="metadata"
                  controlsList="nodownload"
                  className="w-full h-full"
                  data-testid="film-trailer-player"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            </div>
          )}

          {/* DIRECTOR BIO */}
          {creator && (
            <div className="mt-16 p-6 rounded-xl bg-surface border border-white/10" data-testid="director-bio">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-3">About the director</p>
              <div className="flex items-start gap-4">
                <Link to={`/@${creator.handle}`} className="w-14 h-14 rounded-full bg-gradient-to-br from-terracotta to-ember flex items-center justify-center text-white font-display font-black text-xl shrink-0 hover:scale-105 transition-transform">
                  {creator.name?.[0]?.toUpperCase() || "C"}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Link to={`/@${creator.handle}`} className="font-display text-xl font-bold hover:text-terracotta transition-colors">{creator.name}</Link>
                      {creator.location && <span className="text-foreground/50 text-sm ml-2">· {creator.location}</span>}
                    </div>
                    <FollowButton handle={creator.handle} className="h-9 px-4 text-sm" testId="film-creator-follow-btn" />
                  </div>
                  {creator.bio ? (
                    <p className="text-foreground/75 mt-2 leading-relaxed">{creator.bio}</p>
                  ) : (
                    <p className="text-foreground/40 italic mt-2">This filmmaker hasn&apos;t added a bio yet.</p>
                  )}
                  {(creator.roles || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {creator.roles.slice(0, 5).map((r) => (
                        <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-terracotta/15 text-terracotta border border-terracotta/30">{r}</span>
                      ))}
                    </div>
                  )}
                  <Link to={`/@${creator.handle}`} className="inline-block text-sm text-terracotta hover:text-ember mt-3" data-testid="link-creator-profile">
                    View full profile →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* REVIEWS — visible to anyone, editable only for owning viewers */}
          <div className="mt-16" data-testid="film-reviews-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1 flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Viewer reviews
                </p>
                <div className="flex items-center gap-3">
                  <h2 className="font-display text-2xl font-bold">
                    {ratingSummary.count > 0 ? `${ratingSummary.average.toFixed(1)} · ${ratingSummary.count} review${ratingSummary.count === 1 ? "" : "s"}` : "No reviews yet"}
                  </h2>
                  {ratingSummary.count > 0 && (
                    <StarRatingDisplay value={ratingSummary.average} size={18} />
                  )}
                </div>
              </div>
              {canRate && (
                <Button
                  onClick={() => setRatingOpen(true)}
                  variant="outline"
                  className="border-gold/40 text-gold hover:bg-gold/10"
                  data-testid="write-review-btn"
                >
                  <Star className="w-4 h-4" /> Write a review
                </Button>
              )}
            </div>

            {reviews.length === 0 ? (
              <p className="text-foreground/50 italic text-sm">
                {canRate
                  ? "Be the first to leave a review — the filmmaker will see it."
                  : "Reviews from paying viewers show up here once someone rates the film."}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.map((r) => (
                  <div key={r.id} className="p-5 rounded-xl bg-surface border border-white/10" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="text-sm font-semibold text-foreground/90">{r.user_name}</p>
                      <StarRatingDisplay value={r.stars} size={14} />
                    </div>
                    {r.comment && (
                      <p className="text-sm text-foreground/75 leading-relaxed italic">&ldquo;{r.comment}&rdquo;</p>
                    )}
                    <p className="text-[10px] uppercase tracking-widest text-foreground/40 mt-3">
                      {new Date(r.updated_at || r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RELATED FILMS */}
          {related.length > 0 && (
            <div className="mt-16" data-testid="related-films">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1 flex items-center gap-1.5">
                    <FilmIcon className="w-3 h-3" /> More like this
                  </p>
                  <h2 className="font-display text-2xl font-bold">Related shorts</h2>
                </div>
              </div>
              <div className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-3">
                {related.map((f, i) => <FilmCard key={f.id} film={f} index={i} />)}
              </div>
            </div>
          )}
        </div>
      </div>

      {canRate && (
        <RatingDialog
          open={ratingOpen}
          onOpenChange={setRatingOpen}
          film={film}
          onSubmitted={refreshRatings}
        />
      )}

      {user && film && (
        <WatchPartyDialog
          open={partyOpen}
          onOpenChange={setPartyOpen}
          film={film}
          currency={isDiaspora ? "USD" : "NGN"}
          activeParty={activeParty}
        />
      )}

      {/* Donation modal — tip the filmmaker. 70% goes to the filmmaker's
          wallet via the Paystack flow, 30% to the platform. Processed by
          the same webhook pipeline as rentals. */}
      <Dialog open={donateOpen} onOpenChange={setDonateOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-md" data-testid="donate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" /> Support this filmmaker
            </DialogTitle>
            <DialogDescription className="text-foreground/60">
              100% of your tip is split 70/30 — the filmmaker gets 70% straight to their wallet.
            </DialogDescription>
          </DialogHeader>

          <div className={`grid ${isDiaspora ? "grid-cols-3" : "grid-cols-2"} gap-2 mt-2`} data-testid="donate-preset-grid">
            {donatePresets.map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => setDonateAmount(amt)}
                className={[
                  "h-14 rounded-lg border font-display font-bold text-lg transition-all",
                  donateAmount === amt
                    ? "bg-pink-500/15 border-pink-400 text-pink-200"
                    : "bg-ink border-white/10 text-foreground/80 hover:border-pink-400/50",
                ].join(" ")}
                data-testid={`donate-preset-${amt}`}
              >
                {fmtDonate(amt)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setDonateAmount("custom")}
              className={[
                "h-14 rounded-lg border font-semibold text-sm transition-all",
                donateAmount === "custom"
                  ? "bg-pink-500/15 border-pink-400 text-pink-200"
                  : "bg-ink border-white/10 text-foreground/80 hover:border-pink-400/50",
              ].join(" ")}
              data-testid="donate-preset-custom"
            >
              Custom amount
            </button>
          </div>

          {donateAmount === "custom" && (
            <div className="mt-3">
              <label className="text-xs text-foreground/55 uppercase tracking-wider" htmlFor="donate-custom-input">Amount ({isDiaspora ? "$" : "₦"})</label>
              <Input
                id="donate-custom-input"
                type="number"
                min={isDiaspora ? 1 : 100}
                max={isDiaspora ? 1000 : 1000000}
                step={isDiaspora ? 1 : 100}
                value={customDonate}
                onChange={(e) => setCustomDonate(e.target.value)}
                placeholder={isDiaspora ? "e.g. 15" : "e.g. 3000"}
                className="mt-1 bg-ink border-white/15"
                data-testid="donate-custom-input"
              />
              <p className="mt-1 text-[11px] text-foreground/40">{isDiaspora ? "Minimum $1 · maximum $1,000" : "Minimum ₦100 · maximum ₦1,000,000"}</p>
            </div>
          )}

          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setDonateOpen(false)}
              className="h-11 border border-white/10"
              data-testid="donate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDonate}
              disabled={donating}
              className="h-11 bg-pink-500 hover:bg-pink-600 text-white font-semibold"
              data-testid="donate-submit-btn"
            >
              <Heart className="w-4 h-4" />
              {donating ? "Redirecting…" : isDiaspora ? "Donate with Flutterwave" : "Donate with Paystack"}
            </Button>
          </DialogFooter>
          <RefundNote className="text-center sm:text-right" testId="donate-refund-policy-link" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
